import React, { useEffect, useState } from "react";
import { HiChevronDown, HiRefresh } from "react-icons/hi";
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

import API from "../../api";
import { useSocket } from "../../contexts/SocketContext";

const Sync = ({ user }) => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const isSuperAdmin = user.role === "superadmin";
  const [openActionsMenuKey, setOpenActionsMenuKey] = useState(null);

  const { refs, floatingStyles, context } = useFloating({
    open: openActionsMenuKey !== null,
    onOpenChange: (open) => {
      if (!open) setOpenActionsMenuKey(null);
    },
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const { getFloatingProps } = useInteractions([useDismiss(context), useRole(context, { role: "menu" })]);

  const {
    data: plan,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["syncPlan"],
    queryFn: async () => {
      const response = await API.get("/sync/plan");
      return response.data;
    },
    refetchInterval: 15000,
    retry: false,
  });

  useEffect(() => {
    if (!socket) return;

    const onPresence = (presence) => {
      if (presence.indexName === user.indexName) queryClient.invalidateQueries({ queryKey: ["syncPlan"] });
    };

    socket.on("machine:presence", onPresence);
    return () => socket.off("machine:presence", onPresence);
  }, [socket, queryClient, user.indexName]);

  const updateEntryStateMutation = useMutation({
    mutationFn: ({ key, body }) => API.put(`/sync/entry/${key}/state`, body),
    onSuccess: () => {
      toast.success(plan.agentConnected ? "Synchronisation demandée" : "Agent déconnecté : la synchronisation démarrera à sa reconnexion");
      queryClient.invalidateQueries({ queryKey: ["syncPlan"] });
    },
    onError: (e) => toast.error(e.message || "Impossible de demander la synchronisation"),
  });

  const syncAllMutation = useMutation({
    mutationFn: async () => {
      for (const entry of plan.entries.filter((planEntry) => planEntry.enabled && !planEntry.inFlight)) {
        await API.put(`/sync/entry/${entry.key}/state`, { runRequested: true });
      }
    },
    onSuccess: () => toast.success(plan.agentConnected ? "Synchronisation de toutes les entrées demandée" : "Agent déconnecté : les synchronisations démarreront à sa reconnexion"),
    onError: (e) => toast.error(e.message || "Impossible de demander la synchronisation"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["syncPlan"] }),
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({ key, body }) => API.put(`/sync/plan/entry/${key}`, body),
    onSuccess: () => {
      toast.success("Entrée mise à jour");
      queryClient.invalidateQueries({ queryKey: ["syncPlan"] });
    },
    onError: (e) => toast.error(e.message || "Impossible de modifier l'entrée"),
  });

  const onEntryFieldBlur = (entry, field, event) => {
    const input = event.target;
    const value = field === "schedule" ? input.value.trim() : Number(input.value);
    if (value === entry[field]) return;
    updateEntryMutation.mutate(
      { key: entry.key, body: { [field]: value } },
      {
        onError: () => {
          input.value = entry[field];
        },
      },
    );
  };

  const onFullResyncClick = (entry) => {
    setOpenActionsMenuKey(null);
    const confirmMessage =
      entry.queryName === "patients-delta"
        ? "Réimporter les patients ayant une demande ces 5 dernières années ?"
        : `Réimporter toutes les données de "${entry.key}" depuis le début ?`;
    if (!window.confirm(confirmMessage)) return;
    updateEntryStateMutation.mutate({ key: entry.key, body: { syncedAt: null, runRequested: true } });
  };

  const openActionsMenuEntry = plan?.entries.find((entry) => entry.key === openActionsMenuKey);

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900">Synchronisation</h1>
            {plan && (
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                <span className={`h-2 w-2 rounded-full ${plan.agentConnected ? "bg-emerald-500" : "bg-gray-300"}`} />
                {plan.agentConnected ? "Agent connecté" : "Agent déconnecté"}
              </span>
            )}
          </div>
          {plan && (
            <button
              type="button"
              onClick={() => syncAllMutation.mutate()}
              disabled={syncAllMutation.isPending}
              className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <HiRefresh className={`mr-1.5 h-4 w-4 ${syncAllMutation.isPending ? "animate-spin [animation-direction:reverse]" : ""}`} />
              Tout synchroniser
            </button>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
            </div>
          ) : error ? (
            <div className="py-16 text-center text-gray-400">{error.message || "Impossible de charger le plan de synchronisation"}</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Entrée</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2">Planification</th>
                  <th className="px-3 py-2">Chevauchement (min)</th>
                  <th className="px-3 py-2">Taille des lots</th>
                  <th className="px-3 py-2">Actif</th>
                  <th className="px-3 py-2">Dernier passage</th>
                  <th className="px-3 py-2">Statut</th>
                  <th className="px-3 py-2 text-right">Lignes</th>
                  <th className="px-3 py-2">Curseur</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {plan.entries.map((entry) => (
                  <tr key={entry.key} className={entry.enabled ? "" : "bg-gray-50 text-gray-400"}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{entry.key}</div>
                      <div className="text-xs text-gray-500">{entry.entityType}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{entry.mode === "INCREMENTAL" ? "Incrémental" : "Complet"}</td>
                    <td className="px-3 py-2">
                      {isSuperAdmin ? (
                        <input
                          key={`${entry.key}-${entry.schedule}`}
                          type="text"
                          defaultValue={entry.schedule}
                          onBlur={(event) => onEntryFieldBlur(entry, "schedule", event)}
                          onKeyDown={(event) => event.key === "Enter" && event.target.blur()}
                          className="w-32 rounded-md border border-gray-200 px-2 py-1 font-mono text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="font-mono text-xs text-gray-600">{entry.schedule}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {entry.mode === "SNAPSHOT" ? (
                        <span className="text-gray-400">—</span>
                      ) : isSuperAdmin ? (
                        <input
                          key={`${entry.key}-${entry.overlapMinutes}`}
                          type="number"
                          min={0}
                          defaultValue={entry.overlapMinutes}
                          onBlur={(event) => onEntryFieldBlur(entry, "overlapMinutes", event)}
                          onKeyDown={(event) => event.key === "Enter" && event.target.blur()}
                          className="w-20 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="text-gray-600">{entry.overlapMinutes}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {entry.mode === "SNAPSHOT" ? (
                        <span className="text-gray-400">—</span>
                      ) : isSuperAdmin ? (
                        <input
                          key={`${entry.key}-${entry.batchSize}`}
                          type="number"
                          min={1}
                          defaultValue={entry.batchSize}
                          onBlur={(event) => onEntryFieldBlur(entry, "batchSize", event)}
                          onKeyDown={(event) => event.key === "Enter" && event.target.blur()}
                          className="w-24 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="text-gray-600">{entry.batchSize.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={entry.enabled}
                        onChange={() => updateEntryMutation.mutate({ key: entry.key, body: { enabled: !entry.enabled } })}
                        disabled={!isSuperAdmin || updateEntryMutation.isPending}
                        className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500 disabled:opacity-60"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-600" title={entry.lastRunAt && format(new Date(entry.lastRunAt), "dd/MM/yyyy HH:mm:ss")}>
                      {entry.lastRunAt ? formatDistanceToNow(new Date(entry.lastRunAt), { addSuffix: true, locale: fr }) : "Jamais"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {entry.lastRunStatus === "error" && !entry.inFlight && (
                          <span title={entry.lastRunError} className="cursor-help rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                            Erreur
                          </span>
                        )}
                        {entry.inFlight ? (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">En cours</span>
                        ) : entry.due ? (
                          <span
                            title={plan.agentConnected ? "Sera envoyée à l'agent dans la minute" : "Démarrera à la reconnexion de l'agent"}
                            className="cursor-help whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                          >
                            En attente
                          </span>
                        ) : entry.lastRunStatus === "ok" ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">OK</span>
                        ) : entry.lastRunStatus === "error" ? null : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{entry.lastRunRows === undefined ? "—" : entry.lastRunRows.toLocaleString()}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {entry.mode === "SNAPSHOT" ? (
                        <span className="text-gray-400">—</span>
                      ) : entry.syncedAt ? (
                        format(new Date(entry.syncedAt), "dd/MM/yyyy HH:mm")
                      ) : (
                        "Import complet"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {isSuperAdmin && entry.mode === "INCREMENTAL" ? (
                        <div ref={openActionsMenuKey === entry.key ? refs.setReference : undefined} className="inline-flex align-middle">
                          <button
                            type="button"
                            title="Synchroniser maintenant"
                            onClick={() => updateEntryStateMutation.mutate({ key: entry.key, body: { runRequested: true } })}
                            disabled={!entry.enabled || entry.inFlight || updateEntryStateMutation.isPending}
                            className="rounded-l-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Synchroniser
                          </button>
                          <button
                            type="button"
                            aria-label="Plus d'options"
                            onClick={() => setOpenActionsMenuKey(openActionsMenuKey === entry.key ? null : entry.key)}
                            disabled={!entry.enabled || entry.inFlight || updateEntryStateMutation.isPending}
                            className="flex items-center rounded-r-lg border border-l-0 border-gray-200 bg-white px-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <HiChevronDown className={`h-3 w-3 transition-transform ${openActionsMenuKey === entry.key ? "rotate-180" : ""}`} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          title="Synchroniser maintenant"
                          onClick={() => updateEntryStateMutation.mutate({ key: entry.key, body: { runRequested: true } })}
                          disabled={!entry.enabled || entry.inFlight || updateEntryStateMutation.isPending}
                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Synchroniser
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {openActionsMenuEntry && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 60 }} {...getFloatingProps()} className="dropdown-panel w-64 overflow-hidden py-1">
            <button
              type="button"
              onClick={() => onFullResyncClick(openActionsMenuEntry)}
              disabled={!openActionsMenuEntry.enabled || openActionsMenuEntry.inFlight || updateEntryStateMutation.isPending}
              className="flex w-full flex-col px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <span className="font-medium">Depuis le début</span>
              <span className="leading-tight text-gray-500">Réimporte toutes les données</span>
            </button>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default Sync;
